// Resolving a repeating group's options per ROW rather than per column.
//
// A dependent picker narrows against the record it sits in, and inside a grid that record is the
// ROW — the server validates a group by recursing with the element as the whole context, so a
// child field's `parentFrom` names a sibling within the same row and nothing outside it. Resolving
// the column once for the whole grid therefore offers every row the unnarrowed list, which is the
// choice the dependency exists to prevent.
//
// THE CACHE KEY IS THE CONTEXT, NOT THE ROW. Options are a function of the trigger values and
// nothing else, so two rows that answered the trigger the same way share one answer. Ten contacts
// all marked Contract Manufacturer cost one request, not ten — and a row whose triggers have not
// changed costs none at all, which is what keeps this from being a request per keystroke.

export type Trigger = string;

/**
 * Which sibling fields any column depends on.
 *
 * READ FROM AN UNANSWERED DEFINITION. Asked with no context, the server reports every dependent
 * field as waiting and names what it waits for — so one context-free read tells us both which
 * columns are dependent and what their answers hang off, without hard-coding either.
 */
export function dependentTriggers(columns: {RequiresContext?: string[]; [key: string]: any}[]): Trigger[] {
    const triggers = new Set<Trigger>();
    (columns ?? []).forEach(column =>
        (column.RequiresContext ?? []).forEach(code => triggers.add(code)));
    return Array.from(triggers).sort();
}

/** Just the trigger answers from one row — the only part of it that can change what is offered. */
export function contextForRow(element: any, triggers: Trigger[]): Record<string, any> {
    const context: Record<string, any> = {};
    triggers.forEach(code => {
        const value = (element ?? {})[code];
        if (value !== undefined) context[code] = value;
    });
    return context;
}

/**
 * A stable key for a context, so equal contexts share a cached answer.
 *
 * Keys are sorted, because `{a, b}` and `{b, a}` are the same question and would otherwise be asked
 * twice — and asked again on every re-render, since object key order is not something a caller
 * controls.
 */
export function contextKey(context: Record<string, any>): string {
    return JSON.stringify(Object.keys(context).sort().map(code => [code, context[code]]));
}

/** The distinct contexts a set of rows needs answered. One request each, however many rows. */
export function distinctContexts(elements: any[], triggers: Trigger[]):
        {key: string; context: Record<string, any>}[] {
    const seen = new Map<string, Record<string, any>>();
    (elements ?? []).forEach(element => {
        const context = contextForRow(element, triggers);
        const key = contextKey(context);
        if (!seen.has(key)) seen.set(key, context);
    });
    return Array.from(seen, ([key, context]) => ({key, context}));
}
